import { FC, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'app/store';
import { useNavigate } from 'react-router-dom';
import {
  useFacebookAdsAuthenticationStatusQuery,
  useCompleteFacebookAdsIntegrationSetupMutation,
  useFacebookAdsTokenHandlerMutation,
  commandApi
} from 'app/apis/commandApi';
import { selectUserDetails } from 'features/auth/authenticationSlice';
import { useFacebookAuthCodeHandler } from 'common/hooks/clientIntegrations/useFacebookAuthCodeHandler';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import TileGridContainer from 'features/tiles/tileGridContainer';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import { queryApi, useGetClientIntegrationQuery } from 'app/apis/queryApi';
import { openModal } from 'features/modal/modalSlice';
import AuthenticationStatus from 'features/client-integrations/integration-setup/authentication-status';
import AccountConfigurations from 'features/client-integrations/integration-setup/account-id-list-with-filters';
import {
  selectPlatformConfig,
  selectPlatformConfigAccountIds
} from 'features/client-integrations/integration-setup/platform-config/platform-config.slice';

const facebookLogo = '/media/logos/facebook-logo.png';

const CLIENT_ID = process.env.REACT_APP_FACEBOOK_CLIENT_ID!;
const REDIRECT_URI = process.env.REACT_APP_FACEBOOK_REDIRECT_URI!;
const GRAPH_VERSION = 'v23.0';
const SCOPES = 'ads_read';

const platformType = 'FacebookAds';

export const FacebookAdsSetupPage: FC = () => {
  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const userDetails = useSelector(selectUserDetails);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: facebookAdsAuthenticationStatus,
    isFetching: facebookAdsAuthenticationStatusFetching,
    isLoading: facebookAdsAuthenticationStatusLoading,
    refetch: facebookAdsAuthenticationStatusRefetch,
    isSuccess: facebookAdsAuthenticationStatusSuccess
  } = useFacebookAdsAuthenticationStatusQuery(siteId, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true
  });

  const { isLoading: integrationLoading, isFetching: integrationFetching } =
    useGetClientIntegrationQuery(
      {
        siteId,
        platformCategory: 'Ads',
        platform: 'FacebookAds',
        clientIntegrationType: 'AdCost'
      },
      {
        skip: !facebookAdsAuthenticationStatusSuccess,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true
      }
    );

  const [facebookAdsTokenHandler] = useFacebookAdsTokenHandlerMutation();

  const facebookIntegrationConfigs = useSelector(
    selectPlatformConfig(platformType)
  );

  const facebookIntegrationConfigAccountIds = useSelector(
    selectPlatformConfigAccountIds(platformType)
  );

  const handleRemoveAccountId = (accountId: string) => {
    dispatch(
      openModal({
        modalType: 'confirmation',
        modalProps: {
          title: 'Remove Account ID',
          content:
            'Are you sure you want to remove this Facebook Ads Account ID?',
          confirmButtonText: 'Remove',
          confirmButtonTheme: ButtonTheme.PRIMARY,
          cancelButtonText: 'Cancel',
          cancelButtonTheme: ButtonTheme.CANCEL,
          additionalProps: {
            accountId,
            platformType
          }
        }
      })
    );
  };

  const [completeFacebookAdsIntegrationSetup, { isLoading: validateLoading }] =
    useCompleteFacebookAdsIntegrationSetupMutation();

  const { isAuthenticating } = useFacebookAuthCodeHandler({
    siteId,
    facebookAdsTokenHandler,
    facebookAdsAuthenticationStatusRefetch: async () => {
      await facebookAdsAuthenticationStatusRefetch();
    }
  });

  const handleFacebookLogin = () => {
    const state = crypto.randomUUID();
    sessionStorage.setItem('fb_oauth_state', state);

    const authUrl =
      `https://www.facebook.com/${GRAPH_VERSION}/dialog/oauth?` +
      `client_id=${CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
      `&state=${state}` +
      `&scope=${SCOPES}`;

    window.location.href = authUrl;
  };

  const handleSubmit = async () => {
    try {
      setSubmitError(null);
      setSubmitSuccess(null);

      if (
        facebookIntegrationConfigs.length === 0 ||
        facebookIntegrationConfigAccountIds.length === 0
      ) {
        setSubmitError('Please enter at least one Account ID.');
        return;
      }

      await completeFacebookAdsIntegrationSetup({
        siteId,
        accountIds: facebookIntegrationConfigAccountIds,
        createdBy: userDetails.UserName,
        updatedBy: userDetails.UserName,
        platformConfigs: facebookIntegrationConfigs
      }).unwrap();

      setSubmitSuccess(
        `Account IDs: ${facebookIntegrationConfigAccountIds.join(
          ', '
        )} validated successfully!`
      );
    } catch (err: any) {
      const apiError =
        err?.data?.message ||
        err?.error ||
        err?.data ||
        'Something went wrong. Please try again.';
      setSubmitError(apiError);
    }
  };

  const handleFinishClick = () => {
    navigate('/settings/integrations');
    dispatch(queryApi.util.invalidateTags(['ClientIntegration']));
    dispatch(commandApi.util.invalidateTags(['FacebookAuthStatus']));
  };
  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
        <section className="text-primary-700 mx-6 my-6">
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <div className="flex flex-row items-center justify-center space-x-4 border-b border-neutral-200 p-2">
              <img src={facebookLogo} alt="Facebook" className="h-10" />
              <h1 className="text-lg font-semibold">
                Facebook Ads API Integration
              </h1>
            </div>
            <p>
              Step 1. Click on authenticate with Facebook below to allow Ruler
              access to Facebook Ads via OAuth.
            </p>
            <AuthenticationStatus
              isAuthenticating={isAuthenticating}
              authenticationStatusLoading={
                facebookAdsAuthenticationStatusLoading
              }
              authenticationStatusFetching={
                facebookAdsAuthenticationStatusFetching
              }
              authenticationStatus={facebookAdsAuthenticationStatus}
              buttonLogoSrc={facebookLogo}
              platform="Facebook"
              onLogin={handleFacebookLogin}
            />

            {facebookAdsAuthenticationStatus?.isAuthenticated && (
              <div className="border-t border-neutral-200">
                <p className="text-center text-primary-700 mb-4 mt-6">
                  Step 2. To complete the integration, please enter your
                  Facebook Ads Account ID(s).
                </p>

                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="flex flex-col items-center gap-4"
                >
                  <AccountConfigurations
                    platformType={platformType}
                    accountTypeDisplayName="Account"
                    accountIds={facebookIntegrationConfigAccountIds}
                    platformConfigs={facebookIntegrationConfigs}
                    validateLoading={validateLoading}
                    integrationLoading={
                      integrationLoading ||
                      integrationFetching ||
                      isAuthenticating ||
                      facebookAdsAuthenticationStatusFetching
                    }
                    submitError={submitError}
                    submitSuccess={submitSuccess}
                    onRemoveAccountId={handleRemoveAccountId}
                  />

                  <div className="flex space-x-4 mt-4">
                    <Button
                      isLoading={validateLoading}
                      theme={ButtonTheme.SECONDARY}
                      identifier="validate-facebook-ads-setup"
                      onClick={() => {
                        void handleSubmit();
                      }}
                    >
                      <span>Validate</span>
                    </Button>
                    {submitSuccess && (
                      <Button
                        theme={ButtonTheme.PRIMARY}
                        identifier="finish-facebook-ads-setup"
                        onClick={handleFinishClick}
                      >
                        <span>Finish</span>
                      </Button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};
